import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ApiSuccessEnvelope } from '../models/auth.model';

@Injectable({ providedIn: 'root' })
export class ApiService {
  private http = inject(HttpClient);
  private readonly baseUrl = `${environment.baseUrl}/api/v1/creator`;

  get<T>(endpoint: string, params?: Record<string, unknown>): Observable<T> {
    return this.http
      .get<ApiSuccessEnvelope<T>>(`${this.baseUrl}${endpoint}`, { params: this.buildParams(params) })
      .pipe(map(res => res.data));
  }

  // Like get(), but keeps `meta` — needed for paginated endpoints (total/page/totalPages).
  getWithMeta<T>(endpoint: string, params?: Record<string, unknown>): Observable<ApiSuccessEnvelope<T>> {
    return this.http.get<ApiSuccessEnvelope<T>>(`${this.baseUrl}${endpoint}`, { params: this.buildParams(params) });
  }

  post<T>(endpoint: string, body: unknown): Observable<T> {
    return this.http
      .post<ApiSuccessEnvelope<T>>(`${this.baseUrl}${endpoint}`, body)
      .pipe(map(res => res.data));
  }

  put<T>(endpoint: string, body: unknown): Observable<T> {
    return this.http
      .put<ApiSuccessEnvelope<T>>(`${this.baseUrl}${endpoint}`, body)
      .pipe(map(res => res.data));
  }

  patch<T>(endpoint: string, body: unknown): Observable<T> {
    return this.http
      .patch<ApiSuccessEnvelope<T>>(`${this.baseUrl}${endpoint}`, body)
      .pipe(map(res => res.data));
  }

  delete<T>(endpoint: string): Observable<T> {
    return this.http
      .delete<ApiSuccessEnvelope<T>>(`${this.baseUrl}${endpoint}`)
      .pipe(map(res => res.data));
  }

  // Angular's HttpClient.delete() needs an explicit `body` option to send a JSON payload —
  // used by endpoints where DELETE needs more than an id in the URL (e.g. abort upload).
  deleteWithBody<T>(endpoint: string, body: unknown): Observable<T> {
    return this.http
      .delete<ApiSuccessEnvelope<T>>(`${this.baseUrl}${endpoint}`, { body })
      .pipe(map(res => res.data));
  }

  private buildParams(params?: Record<string, unknown>): HttpParams {
    let p = new HttpParams();
    if (params) {
      for (const [key, value] of Object.entries(params)) {
        if (value !== null && value !== undefined) p = p.set(key, String(value));
      }
    }
    return p;
  }
}
